import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import * as Profile from "@/components/auth/Profile";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import * as Skeleton from "@/components/skeleton/Skeleton";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import { SearchResult } from "./SearchResult";

import type { GroupedSearchResult } from "@/store/SearchStore";

const TeamGroup = observer(function TeamGroup({ teamGroup }: { teamGroup: GroupedSearchResult }) {
  const [isOpen, setIsOpen] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const title = teamGroup.title || `@${teamGroup.permalink}`;
  const t = useTranslations();

  const handleTeamClick = () => {
    setIsOpen(!isOpen);
  };

  const handleShowMoreClick = () => {
    setShowMore(!showMore);
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleTeamClick}
        className={twMerge(
          "flex items-center w-full h-[44px] -mt-1.25 cursor-pointer",
          "hover:opacity-80 active:opacity-60",
          "sm:hover:opacity-100 sm:active:opacity-100",
          !isOpen && "-mb-1.25",
        )}
        aria-label={title}
      >
        <div className={twMerge("min-w-5 my-auto")}>
          <Icon
            name="ArrowDownLarge"
            className={twMerge(
              "w-[16px] h-[16px] m-auto [&>path]:stroke-document-text",
              !isOpen && "-rotate-90",
            )}
          />
        </div>
        <span
          className={twMerge(
            "truncate",
            "mr-1.5",
            "text-[15px] leading-[18px] font-semibold tracking-tight text-left text-document-text",
            "sm:text-[17px] sm:leading-[20px]",
          )}
        >
          {title}
        </span>
        <Profile.Userpic
          src={teamGroup.avatarUrls?.[64]}
          name={title}
          setColorFrom={teamGroup.uid}
          className={twMerge(
            "w-[24px] h-[24px] min-w-[24px] min-h-[24px] ml-auto mr-1.25 text-[14px]",
            "sm:w-[30px] sm:h-[30px] sm:min-w-[30px] sm:min-h-[30px] sm:text-[16px]",
          )}
        />
      </button>

      {isOpen && (
        <>
          <div className="h-[1px] mx-2.5 bg-gray-300 dark:bg-gray-700" />
          <div className="mt-1">
            {teamGroup.documents.slice(0, showMore ? teamGroup.documents.length : 2).map((doc) => (
              <SearchResult key={doc.uid} searchResult={doc} />
            ))}
          </div>
          {teamGroup.documents.length > 2 && (
            <button
              type="button"
              onClick={handleShowMoreClick}
              className={twMerge(
                "flex items-center w-full h-[22px] mt-1.25 cursor-pointer",
                "hover:opacity-80 active:opacity-60",
                "sm:hover:opacity-100 sm:active:opacity-100",
              )}
            >
              <div className={twMerge("min-w-5 my-auto")}>
                <Icon
                  name="ArrowDownLarge"
                  className={twMerge(
                    "w-[16px] h-[16px] m-auto [&>path]:stroke-gray-500",
                    showMore ? "-rotate-180" : "-rotate-90",
                  )}
                />
              </div>
              <span className="text-gray-500 text-[14px] font-mono tracking-tight uppercase">
                {showMore ? t("search.showLess") : t("search.showMore")}
              </span>
            </button>
          )}
        </>
      )}
    </div>
  );
});

export const SearchList = observer(function SearchList({
  nested,
}: {
  /** Rendered inside a bordered sidebar group, so it drops its own border */
  nested?: boolean;
}) {
  const { search } = useStore();
  const t = useTranslations();
  const borderClassName = nested && "shadow-none dark:shadow-none";

  if (search.status === "fetching") {
    return (
      <Sidebar.Group className={twMerge("p-1.25", borderClassName)} shrink>
        <Skeleton.Root>
          <Sidebar.GroupSection className="mt-0 p-0">
            <Skeleton.Block className="h-[70px]" />
          </Sidebar.GroupSection>
          <Sidebar.GroupSection className="p-0">
            <Skeleton.Block className="h-[40px]" />
          </Sidebar.GroupSection>
          <Sidebar.GroupSection className="p-0">
            <Skeleton.Block className="h-[40px]" />
          </Sidebar.GroupSection>
        </Skeleton.Root>
      </Sidebar.Group>
    );
  }

  if (search.status === "loaded" && search.documents.length === 0) {
    return (
      <Sidebar.Group className={twMerge("p-1.25", borderClassName)} shrink>
        <div className="w-full text-center text-[16px] leading-[20px] text-gray-500 font-normal tracking-tight">
          {t("search.noResults")}
        </div>
      </Sidebar.Group>
    );
  }

  if (search.status === "error") {
    return (
      <Sidebar.Group className={twMerge("p-1.25", borderClassName)} shrink>
        <div className="w-full text-center text-[16px] leading-[20px] text-gray-500 font-normal tracking-tight">
          {search.error}
        </div>
      </Sidebar.Group>
    );
  }

  if (search.status === "idle" || !search.searchQuery) {
    return null;
  }

  return (
    <Sidebar.Group className={twMerge("p-0", borderClassName)} shrink>
      <Sidebar.Scrollable className="p-1.25">
        {search.groupedDocs.map((teamGroup) => (
          <Sidebar.GroupSection key={teamGroup.uid} className="mt-0 [&:not(:last-child)]:mb-1.25">
            <TeamGroup teamGroup={teamGroup} />
          </Sidebar.GroupSection>
        ))}
      </Sidebar.Scrollable>
    </Sidebar.Group>
  );
});
